// Mock data Service for Demo Mode (Client-Side In-Memory DB)
// Generates engineered data to showcase specific rating scenarios.

// --- Seeded Random Number Generator for Consistent Demo Data ---
// A simple Linear Congruential Generator (LCG)
const createSeededRandom = (seed) => {
  let state = seed;
  const a = 1103515245;
  const c = 12345;
  const m = 2**31;

  return () => {
    state = (a * state + c) % m;
    return state / m;
  };
};

const SEED = 12345;
const seededRandom = createSeededRandom(SEED);

// --- Engineered Player Profiles ---
// We define "archetypes" to tell a story with the data.
const playerArchetypes = [
    { id: 'p1', name: 'Grandmaster Gary', role: 'pro', skill: 0.95, matches: 'high' },
    { id: 'p2', name: 'Elite Elaine', role: 'pro', skill: 0.85, matches: 'high' },
    { id: 'p3', name: 'Veteran Victor', role: 'veteran', skill: 0.70, matches: 'high' },
    { id: 'p4', name: 'Solid Sarah', role: 'veteran', skill: 0.65, matches: 'high' },
    { id: 'p5', name: 'Average Andy', role: 'average', skill: 0.50, matches: 'medium' },
    { id: 'p6', name: 'Casual Cathy', role: 'average', skill: 0.45, matches: 'medium' },
    { id: 'p7', name: 'Inconsistent Ian', role: 'volatile', skill: 0.40, matches: 'medium' },
    { id: 'p8', name: 'Rookie Rachel', role: 'new', skill: 0.50, matches: 'low' },
    { id: 'p9', name: 'Intern Isaac', role: 'new', skill: 0.45, matches: 'low' },
    { id: 'p10', name: 'New Hire Nora', role: 'new', skill: 0.55, matches: 'very_low' },
];

const generatePlayers = () => {
    return playerArchetypes.map((p, i) => ({
        id: `player-${i + 1}`, // Consistent IDs
        authUserId: `demo-${i + 1}`,
        name: p.name,
        initials: p.name.split(' ').map(n => n[0]).join(''),
        // These initial stats will be overwritten by the client-side calculator
        // but we set them to defaults here.
        mu: 25.0,
        sigma: 8.33, 
        wins: 0, 
        losses: 0,
        isAdmin: p.role === 'pro',
        paidFullSlices: Math.floor(seededRandom() * 5),
        paidHalfSlices: Math.floor(seededRandom() * 5),
        email: `${p.name.toLowerCase().replace(' ', '.')}@demo.company`,
        createdAt: '2024-01-01T00:00:00.000Z',
    }));
};

const generateMatches = (players, count = 100) => {
  const matches = [];
  const scores = [
    '10-0', '10-1', '10-2', '10-3', '10-4', 
    '10-5', '10-6', '10-7', '10-8', '10-9'
  ];

  // Helper to get archetype by player ID
  const getArchetype = (pid) => playerArchetypes.find(a => `player-${playerArchetypes.indexOf(a) + 1}` === pid);

  // Weighted Pool for matchmaking frequency
  const weightedPool = [];
  playerArchetypes.forEach((archetype, index) => {
      let weight = 1;
      if (archetype.matches === 'high') weight = 20;
      if (archetype.matches === 'medium') weight = 8;
      if (archetype.matches === 'low') weight = 3;
      if (archetype.matches === 'very_low') weight = 1; // Very few games -> Wide curve

      for(let i=0; i<weight; i++) {
          weightedPool.push(`player-${index + 1}`);
      }
  });

  for (let i = 0; i < count; i++) {
    const baseDate = new Date('2024-09-01');
    const daysToAdd = Math.floor((Date.now() - baseDate.getTime()) / (1000 * 60 * 60 * 24) * seededRandom());
    const matchDate = new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);

    // 1. Pick 4 distinct players based on weighted frequency
    let matchPlayers = [];
    while (matchPlayers.length < 4) {
        const pick = weightedPool[Math.floor(seededRandom() * weightedPool.length)];
        if (!matchPlayers.includes(pick)) matchPlayers.push(pick);
    }
    
    // 2. Shuffle teams randomly initially
    matchPlayers.sort(() => seededRandom() - 0.5);
    const t1p1 = matchPlayers[0], t1p2 = matchPlayers[1];
    const t2p1 = matchPlayers[2], t2p2 = matchPlayers[3];

    // 3. Determine Winner based on SKILL
    const team1Skill = getArchetype(t1p1).skill + getArchetype(t1p2).skill;
    const team2Skill = getArchetype(t2p1).skill + getArchetype(t2p2).skill;
    
    // Skill differential determines win probability
    const diff = team1Skill - team2Skill; 
    // If diff is 0, 50% chance. If diff is +0.5, high chance team 1 wins.
    const winProb = 0.5 + (diff * 0.8); // 0.8 is a sensitivity factor

    const team1Wins = seededRandom() < winProb;
    
    // 4. Generate Score (Better winners win harder)
    const winningScore = 10;
    // If it was a close match (low skill diff), loser score is high (8, 9).
    // If stomp (high skill diff), loser score is low (0, 1, 2).
    const skillGap = Math.abs(diff);
    let losingScore = Math.floor(seededRandom() * 9); // Default random
    if (skillGap > 0.4) losingScore = Math.floor(seededRandom() * 3); // Stomp
    if (skillGap < 0.1) losingScore = 8 + Math.floor(seededRandom() * 2); // Close game

    const finalScore = team1Wins ? `10-${losingScore}` : `${losingScore}-10`;
    const team1Score = team1Wins ? 10 : losingScore;
    const team2Score = team1Wins ? losingScore : 10;

    matches.push({
      id: `match-${i + 1}`,
      date: matchDate.toISOString().split('T')[0],
      timestamp: matchDate.toISOString(),
      team1Players: [t1p1, t1p2],
      team2Players: [t2p1, t2p2],
      team1Score,
      team2Score,
      winners: team1Wins ? [t1p1, t1p2] : [t2p1, t2p2],
      losers: team1Wins ? [t2p1, t2p2] : [t1p1, t1p2],
      score: finalScore,
      submittedBy: t1p1,
      submittedByName: players.find(p => p.id === t1p1).name,
      submittedByEmail: players.find(p => p.id === t1p1).email,
      verified: true,
      isVerified: true
    });
  }
  return matches.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

const generateBreadLogs = (players, matches) => {
  const logs = [];
  matches.slice(0, 30).forEach((m, i) => {
     // Generate bread debts for 10-0 or 10-1 losses
     const isStomp = (m.team1Score === 10 && m.team2Score <= 1) || (m.team2Score === 10 && m.team1Score <= 1);
     
     if (isStomp) {
         const submitter = players.find(p => p.id === m.submittedBy);
         const loser = m.losers[0]; // Just charge the first loser for demo simplicity
         
         logs.push({
             id: `bread-${i}`,
             timestamp: m.timestamp,
             playerId: loser,
             playerName: players.find(p => p.id === loser).name,
             type: 'Full Slice Eaten',
             action: 'earned',
             adminName: 'John Wick',
             adminEmail: 'john.wick@demo.company',
             adminId: m.submittedBy
         });
     }
  });
  return logs;
};

// --- In-Memory State ---

let playersStore = generatePlayers();
let matchesStore = generateMatches(playersStore);
let breadLogsStore = generateBreadLogs(playersStore, matchesStore);

// --- Accessors / Mutators ---

export const mockDB = {
  getPlayers: () => [...playersStore],
  
  addPlayer: (name) => {
    const newPlayer = {
      id: `demo-player-${Date.now()}`,
      name,
      initials: name.substring(0, 2).toUpperCase(),
      mu: 25.0,
      sigma: 8.33,
      wins: 0,
      losses: 0,
      paidFullSlices: 0,
      paidHalfSlices: 0,
      createdAt: new Date().toISOString()
    };
    playersStore = [...playersStore, newPlayer];
    return newPlayer;
  },

  getMatches: () => [...matchesStore],

  addMatch: (matchData) => {
    const newMatch = {
      id: `demo-match-${Date.now()}`,
      ...matchData,
      verified: true,
      submittedByName: matchData.submittedByName || 'Unknown',
      submittedByEmail: matchData.submittedByEmail || 'unknown@office-arena.local'
    };
    matchesStore = [newMatch, ...matchesStore];
    // In a real app we'd update ratings here, but for demo UI test, adding match is enough
    const winners = matchData.winners;
    const losers = matchData.losers;
    
    playersStore = playersStore.map(p => {
        if (winners.includes(p.id)) return { ...p, wins: p.wins + 1 };
        if (losers.includes(p.id)) return { ...p, losses: p.losses + 1 };
        return p;
    });
    
    return newMatch;
  },

  getBreadLogs: () => [...breadLogsStore],

  addBreadLog: (logData) => {
    const newLog = {
      id: `demo-bread-${Date.now()}`,
      ...logData,
             adminName: 'John Wick',
             adminEmail: 'john.wick@demo.company',
      timestamp: new Date().toISOString()
    };
    breadLogsStore = [newLog, ...breadLogsStore];
    
    // Update player debt
    const type = logData.type.includes('Full') ? 'paidFullSlices' : 'paidHalfSlices';
    playersStore = playersStore.map(p => {
        if (p.id === logData.playerId) {
            return { ...p, [type]: (p[type] || 0) + 1 };
        }
        return p;
    });
    
    return newLog;
  }
};

// Export static for initial load if needed, but prefer mockDB methods
export const mockPlayers = playersStore;
export const mockMatches = matchesStore;
export const mockBreadLogs = breadLogsStore;